/* global SideDrawer */

class CartDrawer extends SideDrawer {
  connectedCallback() {
    this.bindEvents();
    // Capture before bubbling app handlers: unsaved quantities must not leave the Lunya drawer.
    this.pendingQuantityNavigationHandler = this.preventPendingQuantityNavigation.bind(this);
    this.addEventListener('click', this.pendingQuantityNavigationHandler, true);
    this.addEventListener('auxclick', this.pendingQuantityNavigationHandler, true);
    this.addEventListener('contextmenu', this.pendingQuantityNavigationHandler, true);
    this.quantityStatusHandler = this.updateQuantityStatus.bind(this);
    ['on:cart:after-merge', 'on:cart:error', 'on:cart:quantity-pending'].forEach((event) => {
      this.addEventListener(event, this.quantityStatusHandler);
    });
  }

  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    this.addEventListener('on:cart-drawer:before-open', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
    });
    this.addEventListener('on:cart:after-merge', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.pendingQuantityNavigationHandler, true);
    this.removeEventListener('auxclick', this.pendingQuantityNavigationHandler, true);
    this.removeEventListener('contextmenu', this.pendingQuantityNavigationHandler, true);
    ['on:cart:after-merge', 'on:cart:error', 'on:cart:quantity-pending'].forEach((event) => {
      this.removeEventListener(event, this.quantityStatusHandler);
    });
    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
  }

  preventPendingQuantityNavigation(evt) {
    const form = this.querySelector('cart-form.lunya-cart-drawer');
    const link = evt.target.closest('a[href]');
    if (!form || !link || !form.contains(link)) return;
    // Primary quantity/remove actions stay with CartForm's native AJAX handlers.
    if (evt.type === 'click' && evt.button === 0 && link.matches('.quantity-up, .quantity-down, .cart-item__remove')
      && link.getAttribute('aria-disabled') !== 'true') return;

    if (this.hasPendingQuantity(form)) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      this.showQuantityStatus(form);
    }
  }

  hasPendingQuantity(form) {
    return form.quantityUpdateFailed || form.quantityUpdateScheduled || form.quantityUpdatesInFlight > 0
      || form.pendingQuantityRemovals?.size > 0 || form.classList.contains('cart-form--refreshing')
      || [...form.querySelectorAll('.cart-item__quantity-input:not([disabled])')]
        .some(input => input.value !== input.dataset.initialValue);
  }

  showQuantityStatus(form) {
    const status = form.querySelector('[data-lunya-cart-status]');
    if (status) status.textContent = form.quantityUpdateFailed ? status.dataset.errorMessage : status.dataset.savingMessage;
  }

  updateQuantityStatus(evt) {
    const form = evt.target.closest('cart-form.lunya-cart-drawer');
    if (!form) return;
    const status = form.querySelector('[data-lunya-cart-status]');
    if (!status) return;
    if (!this.hasPendingQuantity(form)) status.textContent = '';
    else if (evt.type !== 'on:cart:after-merge') this.showQuantityStatus(form);
  }

  /**
   * Handle when the drawer is opened via an event
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
  }

  /**
   * Trigger refresh of contents
   */
  cartRefreshHandler() {
    this.querySelector('cart-form').refresh();
  }

  /**
   * Update section's cart-form element with new contents
   * @param {string} html - Whole-section HTML.
   * @param {number} [cartRefreshVersion] - Cart version when the add request started.
   */
  updateFromCartChange(html, cartRefreshVersion) {
    const form = this.querySelector('cart-form');
    if (typeof html !== 'string' || !html.trim()) {
      form.refresh();
      return;
    }
    const needsRefresh = form.classList.contains('lunya-cart-drawer')
      && (cartRefreshVersion === undefined || cartRefreshVersion !== (form.cartRefreshVersion || 0));
    form.refreshFromHtml(html);
    // A late bundle may predate a newer snapshot, including one already applied.
    if (needsRefresh && !form.classList.contains('cart-form--refreshing')) form.refresh();
  }
}

window.customElements.define('cart-drawer', CartDrawer);
